import dayjs, { type Dayjs } from 'dayjs';
import { readonly } from 'vue';

export interface IDateRangePreset {
  start: Dayjs;
  end: Dayjs | null;
  query: string;
}

export type DateRange = { [key: string]: (now: Dayjs) => IDateRangePreset };

export const defaultPresets = {
  last15min: (now: Dayjs) => ({ start: now.subtract(15, 'minute'), end: null, query: 'now-15m/m' }),
  last30min: (now: Dayjs) => ({ start: now.subtract(30, 'minute'), end: null, query: 'now-30m/m' }),
  last1h: (now: Dayjs) => ({ start: now.subtract(1, 'hour'), end: null, query: 'now-1h' }),
  last4h: (now: Dayjs) => ({ start: now.subtract(4, 'hour'), end: null, query: 'now-4h' }),
  last6h: (now: Dayjs) => ({ start: now.subtract(6, 'hour'), end: null, query: 'now-6h' }),
  last12h: (now: Dayjs) => ({ start: now.subtract(12, 'hour'), end: null, query: 'now-12h' }),
  today: (now: Dayjs) => ({ start: now.startOf('day'), end: null, query: 'now/d' }),
  yesterday: (now: Dayjs) => ({
    start: now.startOf('day').subtract(1, 'day'),
    end: now.endOf('day').subtract(1, 'day'),
    query: 'now-1d/d',
  }),
  last3d: (now: Dayjs) => ({ start: now.subtract(3, 'days'), end: null, query: 'now-3d/d' }),
  last7d: (now: Dayjs) => ({ start: now.subtract(7, 'days'), end: null, query: 'now-7d/d' }),
  last14d: (now: Dayjs) => ({ start: now.subtract(14, 'days'), end: null, query: 'now-14d/d' }),
  thisWeek: (now: Dayjs) => ({ start: now.startOf('week'), end: null, query: 'now/w' }),
  lastWeek: (now: Dayjs) => ({
    start: now.startOf('week').subtract(1, 'week'),
    end: now.endOf('week').subtract(1, 'week'),
    query: 'now-1w/w',
  }),
  thisMonth: (now: Dayjs) => ({ start: now.startOf('month'), end: null, query: 'now/M' }),
  lastMonth: (now: Dayjs) => ({
    start: now.startOf('month').subtract(1, 'month'),
    end: now.endOf('month').subtract(1, 'month'),
    query: 'now-1M/M',
  }),
  thisYear: (now: Dayjs) => ({ start: now.startOf('year'), end: null, query: 'now/y' }),
  lastYear: (now: Dayjs) => ({
    start: now.startOf('year').subtract(1, 'year'),
    end: now.endOf('year').subtract(1, 'year'),
    query: 'now-1y/y',
  }),
};

// Composable for date range presets and getting date-range query string for Onify API requests
export function useHDateRangePresets<T extends DateRange | typeof defaultPresets>(
  // Default presets
  ranges?: T,
) {
  const _ranges: T extends undefined ? typeof defaultPresets : T = ranges || (defaultPresets as any);

  // Returns [ startDate, endDate, queryString ] for a given preset key
  function getPresetRange(key: keyof typeof _ranges, now = dayjs()): { start: Dayjs; end: Dayjs | null; query: string } | undefined {
    const range = _ranges[key] as Function | undefined;
    if (range) {
      return range(now);
    }
  }

  return {
    ranges: readonly(_ranges),
    getPresetRange,
  };
}
